import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faGlobe,
  faSearch,
  faStar,
  faChevronDown,
  faChevronRight,
  faEyeSlash,
  faCalendarAlt,
  faUsers,
  faUser,
  faCheck,
  faClock,
  faHistory,
  faLock,
} from "@fortawesome/free-solid-svg-icons";
import { useUserById } from "../../hooks/user/useUserById";
import VerificationBadge from "../user/VerificationBadge";

/**
 * FriendsGlobalView - Updated mockup-style "Friends" tab for the Scorecard page.
 * Shows current week's global competition with friend submissions and quick stats.
 */
const FriendsGlobalView = ({
  submissions,
  eventName,
  eventDate,
  isEventStarted,
  totalEntries,
  events,
  selectedEventId,
  onEventChange,
}) => {
  const [expandedFriend, setExpandedFriend] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showEventHistory, setShowEventHistory] = useState(false);

  const submittedCount = submissions?.length || 0;

  const filteredSubmissions = submissions || [];

  // Determine event status
  const getEventStatus = (event) => {
    if (!event) return "upcoming";
    const eventDateTime = new Date(event.DateTime);
    const now = new Date();
    if (eventDateTime > now) return "upcoming";
    const hoursSinceStart = (now - eventDateTime) / (1000 * 60 * 60);
    if (hoursSinceStart < 24) return "live";
    return "completed";
  };

  const getEventStatusIcon = (status) => {
    switch (status) {
      case "live":
        return (
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-deep-red opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-deep-red"></span>
          </span>
        );
      case "completed":
        return (
          <FontAwesomeIcon icon={faCheck} className="w-3 h-3 text-green-500" />
        );
      default:
        return (
          <FontAwesomeIcon
            icon={faClock}
            className="w-3 h-3 text-medium-grey"
          />
        );
    }
  };

  return (
    <div className="space-y-4">
      {/* Current Week Event Card */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        {/* Top section */}
        <div className="bg-gradient-to-r from-deep-red/10 via-deep-red/5 to-transparent p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-deep-red/10 flex items-center justify-center border border-deep-red/20">
              <FontAwesomeIcon
                icon={faGlobe}
                className="w-5 h-5 text-deep-red"
              />
            </div>
            <div className="flex-1">
              <p className="text-[10px] text-medium-grey uppercase tracking-widest">
                This Week
              </p>
              <h2 className="font-bold text-lg text-dark-grey">
                Global Competition
              </h2>
            </div>
            {isEventStarted && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-deep-red/10 text-deep-red border border-deep-red/20">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-deep-red opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-deep-red"></span>
                </span>
                Live
              </span>
            )}
          </div>
        </div>

        {/* Event Details - Clickable to show history */}
        {events && events.length > 0 && (
          <div className="relative">
            <button
              onClick={() => setShowEventHistory(!showEventHistory)}
              className="w-full px-4 py-3 border-t border-gray-100 bg-light-grey/30 flex items-center gap-3 text-left"
            >
              <FontAwesomeIcon
                icon={faCalendarAlt}
                className="w-4 h-4 text-medium-grey"
              />
              <div className="flex-1">
                <p className="text-sm font-semibold text-dark-grey">
                  {eventName || "Select Event"}
                </p>
                {eventDate && (
                  <p className="text-xs text-medium-grey">{eventDate}</p>
                )}
              </div>
              <FontAwesomeIcon
                icon={faChevronDown}
                className={`w-3.5 h-3.5 text-medium-grey transition-transform ${showEventHistory ? "rotate-180" : ""}`}
              />
            </button>

            {/* Event History Dropdown */}
            {showEventHistory && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl border border-gray-100 shadow-xl z-10 overflow-hidden">
                <div className="p-3 border-b border-gray-100 flex items-center gap-2">
                  <FontAwesomeIcon
                    icon={faHistory}
                    className="w-4 h-4 text-medium-grey"
                  />
                  <p className="text-sm font-semibold text-dark-grey">
                    Event History
                  </p>
                </div>
                <div className="max-h-72 overflow-y-auto">
                  {events.map((event) => {
                    const status = getEventStatus(event);
                    const isSelected =
                      event.EventId === selectedEventId ||
                      event.EventId === parseInt(selectedEventId);
                    return (
                      <button
                        key={event.EventId}
                        onClick={() => {
                          onEventChange(event.EventId);
                          setShowEventHistory(false);
                        }}
                        className={`w-full p-3 flex items-center gap-3 text-left transition-colors hover:bg-light-grey/50 border-b border-gray-50 last:border-0 ${
                          isSelected ? "bg-deep-red/5" : ""
                        }`}
                      >
                        <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                          {getEventStatusIcon(status)}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-dark-grey text-sm">
                              {event.Name}
                            </p>
                            {status === "live" && (
                              <span className="text-[10px] text-deep-red font-semibold uppercase">
                                Now
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-medium-grey">
                            {new Date(event.DateTime).toLocaleDateString(
                              "en-US",
                              { month: "short", day: "numeric" },
                            )}
                          </p>
                        </div>
                        {isSelected && (
                          <FontAwesomeIcon
                            icon={faChevronRight}
                            className="w-3 h-3 text-deep-red"
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Stats Row */}
        <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FontAwesomeIcon
              icon={faUsers}
              className="w-4 h-4 text-medium-grey"
            />
            <span className="text-sm text-dark-grey">
              <span className="font-semibold">{submittedCount}</span>{" "}
              <span className="text-medium-grey">friends entered</span>
            </span>
          </div>
          {totalEntries > 0 && (
            <div className="text-right">
              <p className="text-[10px] text-medium-grey">Global Entries</p>
              <p className="text-sm font-semibold text-deep-red">
                {totalEntries.toLocaleString()}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <FontAwesomeIcon
          icon={faSearch}
          className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-medium-grey"
        />
        <input
          type="text"
          placeholder="Search friends..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm placeholder:text-medium-grey focus:outline-none focus:ring-2 focus:ring-deep-red/50 transition-all"
        />
      </div>

      {/* Friends Submissions */}
      <div className="space-y-3">
        <h3 className="text-xs font-bold text-medium-grey uppercase tracking-wide px-1">
          Friend Submissions
        </h3>

        {filteredSubmissions.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-8 text-center">
            <p className="text-sm text-medium-grey">No friends found</p>
          </div>
        ) : (
          filteredSubmissions.map((submission, index) => {
            const userId =
              typeof submission.userId === "object" &&
              submission.userId !== null
                ? submission.userId._id
                : submission.userId;

            return (
              <FriendSubmissionCard
                key={userId || index}
                submission={submission}
                isEventStarted={isEventStarted}
                isExpanded={expandedFriend === userId}
                onToggle={() =>
                  setExpandedFriend(expandedFriend === userId ? null : userId)
                }
                searchQuery={searchQuery}
              />
            );
          })
        )}
      </div>
    </div>
  );
};

/**
 * Individual friend submission card with quick stats and expandable lineup
 */
const FriendSubmissionCard = ({
  submission,
  isEventStarted,
  isExpanded,
  onToggle,
  searchQuery,
}) => {
  const navigate = useNavigate();

  const userId =
    typeof submission.userId === "object" && submission.userId !== null
      ? submission.userId._id
      : submission.userId;

  const populatedUser =
    typeof submission.userId === "object" && submission.userId !== null
      ? submission.userId
      : null;

  const { data: fetchedUser } = useUserById(populatedUser ? null : userId);
  const user = populatedUser || fetchedUser;

  const displayName = user?.username || user?.name || "Loading...";

  // Filter by search
  if (
    searchQuery &&
    !displayName.toLowerCase().includes(searchQuery.toLowerCase())
  ) {
    return null;
  }

  const selections = submission.selections || [];
  const alternates = submission.alternates || [];
  const projectedTotal = submission.projectedTotal || 0;
  const hasSubmitted = selections.length > 0;

  // Count wins/losses/live for quick stats
  const wins = selections.filter((s) => {
    const outcome = s.lastUpdate?.outcome || "Pending";
    return outcome.toLowerCase().includes("win");
  }).length;

  const losses = selections.filter((s) => {
    const outcome = s.lastUpdate?.outcome || "Pending";
    return outcome.toLowerCase().includes("loss");
  }).length;

  const live = selections.filter((s) => {
    const outcome = s.lastUpdate?.outcome || "Pending";
    return isEventStarted && outcome === "Pending";
  }).length;

  // Find LOTN fighter
  const lotnFighter = selections.find((s) => s.lotn || s.lastUpdate?.isLotn);

  const getStatusStyle = (selection) => {
    const outcome = selection.lastUpdate?.outcome || "Pending";
    if (outcome.toLowerCase().includes("win"))
      return "bg-green-100 text-green-700 border-green-200";
    if (outcome.toLowerCase().includes("loss"))
      return "bg-red-100 text-red-700 border-red-200";
    if (isEventStarted && outcome === "Pending")
      return "bg-deep-red/10 text-deep-red border-deep-red/20";
    return "bg-gray-100 text-medium-grey border-gray-200";
  };

  const getStatusLabel = (selection) => {
    const outcome = selection.lastUpdate?.outcome || "Pending";
    if (outcome.toLowerCase().includes("win")) return "Won";
    if (outcome.toLowerCase().includes("loss")) return "Lost";
    if (isEventStarted && outcome === "Pending") return "Live";
    return "Pending";
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden transition-all">
      {/* Header Row */}
      <button
        onClick={onToggle}
        className="w-full p-4 flex items-center gap-3 text-left"
      >
        {/* Avatar */}
        <div
          className="w-11 h-11 rounded-full bg-gradient-to-br from-deep-red/20 to-deep-red/5 flex items-center justify-center text-sm font-bold border border-deep-red/15 overflow-hidden flex-shrink-0 cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            if (userId) navigate(`/users/${userId}`);
          }}
        >
          {user?.picture ? (
            <img
              src={user.picture}
              alt={displayName}
              className="w-full h-full object-cover"
            />
          ) : (
            <FontAwesomeIcon icon={faUser} className="w-4 h-4 text-gray-400" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="font-semibold text-dark-grey truncate text-sm">
              {displayName}
            </span>
            {user?.verified && (
              <VerificationBadge
                verified={user.verified}
                size="small"
                showTooltip={false}
              />
            )}
          </div>
          {user?.username && user?.name && user.username !== user.name && (
            <p className="text-xs text-medium-grey">@{user.username}</p>
          )}
        </div>

        {/* Quick Stats or No Entry Badge */}
        {hasSubmitted ? (
          <div className="flex items-center gap-1.5 mr-2">
            {isEventStarted ? (
              <>
                {wins > 0 && (
                  <span className="text-xs font-medium text-green-700 bg-green-100 px-1.5 py-0.5 rounded">
                    {wins}W
                  </span>
                )}
                {losses > 0 && (
                  <span className="text-xs font-medium text-red-700 bg-red-100 px-1.5 py-0.5 rounded">
                    {losses}L
                  </span>
                )}
                {live > 0 && (
                  <span className="text-xs font-medium text-deep-red bg-deep-red/10 px-1.5 py-0.5 rounded flex items-center gap-1">
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-deep-red opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-deep-red"></span>
                    </span>
                    {live}
                  </span>
                )}
              </>
            ) : (
              <span className="flex items-center gap-1 text-xs text-medium-grey">
                <FontAwesomeIcon icon={faLock} className="w-3 h-3" />
                {projectedTotal.toFixed(2)} pts
              </span>
            )}
          </div>
        ) : (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium text-red-600 border border-red-200 mr-2">
            <FontAwesomeIcon icon={faEyeSlash} className="w-3 h-3" />
            No Entry
          </span>
        )}

        <FontAwesomeIcon
          icon={faChevronDown}
          className={`w-4 h-4 text-medium-grey transition-transform ${
            isExpanded ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Expanded - Lineup Details (only after event starts) */}
      {isExpanded && hasSubmitted && isEventStarted && (
        <div className="px-4 pb-4 space-y-4">
          {/* Lock of the Night */}
          {lotnFighter && (
            <div className="bg-gradient-to-r from-gold/10 to-gold/5 rounded-xl p-3 border border-gold/20">
              <div className="flex items-center gap-2 mb-3">
                <FontAwesomeIcon icon={faStar} className="w-4 h-4 text-gold" />
                <span className="text-xs font-bold text-gold uppercase tracking-wide">
                  Lock of the Night
                </span>
                <span className="ml-auto inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-gold/20 text-gold border border-gold/30">
                  2x PTS
                </span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-gold/10 flex items-center justify-center border border-gold/20">
                  <FontAwesomeIcon
                    icon={faUser}
                    className="w-5 h-5 text-gold/60"
                  />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-sm text-dark-grey">
                    {lotnFighter.fighter}
                  </p>
                  <p className="text-xs text-medium-grey">
                    {lotnFighter.opponent ? `vs ${lotnFighter.opponent}` : ""}
                  </p>
                </div>
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold capitalize border ${getStatusStyle(lotnFighter)}`}
                >
                  {getStatusLabel(lotnFighter) === "Live" && (
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-deep-red opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-deep-red"></span>
                    </span>
                  )}
                  {getStatusLabel(lotnFighter)}
                </span>
              </div>
            </div>
          )}

          {/* Selections */}
          <div>
            <p className="text-xs font-bold text-medium-grey mb-2 uppercase tracking-wide">
              Selections
            </p>
            <div className="space-y-2">
              {selections.map((selection, idx) => (
                <div
                  key={selection._id || idx}
                  className="flex items-center gap-3 p-2 rounded-lg bg-light-grey/50 border border-gray-100"
                >
                  <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <FontAwesomeIcon
                      icon={faUser}
                      className="w-4 h-4 text-gray-400"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-dark-grey truncate">
                      {selection.fighter}
                    </p>
                    <p className="text-xs text-medium-grey">
                      {selection.opponent
                        ? `vs ${selection.opponent}`
                        : `Pick #${idx + 1}`}
                    </p>
                  </div>
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold capitalize border ${getStatusStyle(selection)}`}
                  >
                    {getStatusLabel(selection) === "Live" && (
                      <span className="relative flex h-1.5 w-1.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-deep-red opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-deep-red"></span>
                      </span>
                    )}
                    {getStatusLabel(selection)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Alternates */}
          {alternates.length > 0 && (
            <div>
              <p className="text-xs font-bold text-medium-grey mb-2 uppercase tracking-wide">
                Alternates
              </p>
              <div className="space-y-2">
                {alternates.map((alt, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-3 p-2 rounded-lg bg-gray-50/50 border border-dashed border-gray-200 opacity-70"
                  >
                    <div className="w-10 h-10 rounded-lg bg-gray-100/50 flex items-center justify-center flex-shrink-0">
                      <FontAwesomeIcon
                        icon={faUser}
                        className="w-4 h-4 text-gray-300"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-medium-grey truncate">
                        {alt.fighter}
                      </p>
                      <p className="text-xs text-medium-grey">
                        {alt.opponent ? `vs ${alt.opponent}` : ""}
                      </p>
                    </div>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border border-gray-200 text-medium-grey">
                      Bench
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Pre-event hidden state */}
      {isExpanded && hasSubmitted && !isEventStarted && (
        <div className="px-4 pb-4">
          <div className="flex flex-col items-center justify-center py-8 text-center bg-light-grey rounded-xl border border-dashed border-gray-200">
            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
              <FontAwesomeIcon
                icon={faLock}
                className="w-5 h-5 text-medium-grey"
              />
            </div>
            <p className="font-medium text-sm text-dark-grey">Picks Hidden</p>
            <p className="text-xs text-medium-grey mt-1">
              Visible once event starts
            </p>
            <div className="mt-4 px-4 py-2 bg-white rounded-lg border border-gray-100">
              <p className="text-xs text-medium-grey">Projected Total</p>
              <p className="font-bold text-lg text-dark-grey">
                {projectedTotal.toFixed(2)} pts
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Not submitted state */}
      {isExpanded && !hasSubmitted && (
        <div className="px-4 pb-4">
          <div className="flex flex-col items-center justify-center py-8 text-center bg-red-50/50 rounded-xl border border-dashed border-red-200/50">
            <div className="w-12 h-12 rounded-full bg-red-100/50 flex items-center justify-center mb-3">
              <FontAwesomeIcon
                icon={faEyeSlash}
                className="w-5 h-5 text-red-400"
              />
            </div>
            <p className="font-medium text-sm text-red-600">No Entry</p>
            <p className="text-xs text-medium-grey mt-1">
              Did not enter this week's competition
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default FriendsGlobalView;
